import React, { useEffect, useMemo, useState } from "react";
import {
  listVaccines,
  listBatchesIdName,
  createVaccineSchedule,
  listVaccineTasks,
  markVaccineTaskGiven,
  markVaccineTasksGiven,
  skipVaccineTask,
  skipVaccineTasks,
} from "../../../shared/api/animal/vaccineService";
import vaccineGroupService from "../../../shared/api/animal/vaccineGroupService";
import inventoryService from "../../../shared/api/animal/inventoryService";

const SPECIES = ["GOAT", "SHEEP", "CATTLE", "CAMEL"];
const PROJECT_TYPES = ["FATTENING", "BREEDING", "DAIRY"];
const STATUSES = ["", "PENDING", "UPCOMING", "GIVEN", "SKIPPED"];

function today() {
  return new Date().toISOString().slice(0, 10);
}


function nice(value) {
  return String(value || "-").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function sameProjectType(taskValue, selectedValue) {
  if (!taskValue || !selectedValue) return true;
  const task = String(taskValue).toUpperCase();
  const selected = String(selectedValue).toUpperCase();
  return task === selected || (selected === "FATTENING" && task === "MEAT") || (selected === "MEAT" && task === "FATTENING");
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function valueOf(v, keys = []) {
  for (const k of keys) {
    if (v?.[k] !== undefined && v?.[k] !== null) return v[k];
  }
  return undefined;
}

function normalizeVaccine(v) {
  const boosterDays = valueOf(v, ["boosterAfterDays", "boosterDays"]);
  const repeatDays = valueOf(v, ["repeatEveryDays", "repeatIntervalDays"]);
  return {
    ...v,
    vaccineName: v?.vaccineName || v?.name || "",
    hasBooster: Boolean(valueOf(v, ["hasBooster"]) ?? (boosterDays > 0)),
    boosterAfterDays: boosterDays,
    hasRepeat: Boolean(valueOf(v, ["hasRepeat"]) ?? (repeatDays > 0)),
    repeatEveryDays: repeatDays,
  };
}

function typeLabel(type) {
  if (type === "INITIAL" || type === "PRIMARY") return "Initial dose";
  if (type === "BOOSTER") return "Booster";
  if (type === "REPEAT_REMINDER" || type === "REPEAT") return "🔔 Repeat reminder";
  return nice(type);
}

function statusLabel(status) {
  if (status === "PENDING" || status === "TODAY" || status === "MISSED") return "⏳ Pending";
  if (status === "GIVEN" || status === "COMPLETED") return "✓ Given";
  if (status === "SKIPPED") return "— Skipped";
  if (status === "UPCOMING") return "Upcoming";
  return nice(status);
}

function animalKey(task) {
  return task.animalId || task.livestockId || task.assetId || task.animalTag || task.animalName || "all";
}

function combineVaccineTasks(rows) {
  const groups = new Map();
  (rows || []).forEach((task) => {
    const date = String(task.dueDate || task.taskDate || task.date || "").slice(0, 10);
    const allocation = task.vaccineGroupId || task.vaccinationRuleId || task.groupId || task.scheduleRequestKey || task.requestKey || task.notes || "same-allocation";
    const key = [date, task.batchId || task.batchName, animalKey(task), task.taskType || "INITIAL", task.status || "", allocation].join("|");
    if (!groups.has(key)) groups.set(key, { ...task, combinationKey:key, taskIds:[], components:[] });
    const group = groups.get(key);
    group.taskIds.push(task.id);
    group.components.push(task);
  });
  return [...groups.values()].map(group => ({
    ...group,
    combinationName:[...new Set(group.components.map(component=>component.vaccineName).filter(Boolean))].join(" + ") || "-",
  }));
}

function Badge({ kind, children }) {
  return <span className={`sched-badge ${kind || ""}`}>{children}</span>;
}

export default function VaccineSchedulePage() {
  const [species, setSpecies] = useState("GOAT");
  const [projectType, setProjectType] = useState("FATTENING");
  const [applyTo, setApplyTo] = useState("ALL");
  const [batchId, setBatchId] = useState("");
  const [vaccineId, setVaccineId] = useState("");
  const [vaccineGroups,setVaccineGroups]=useState([]);
  const [selectionType,setSelectionType]=useState("GROUP");
  const [vaccineGroupId,setVaccineGroupId]=useState("");
  const [ruleSelection,setRuleSelection]=useState("");
  const [scheduleRequestKey,setScheduleRequestKey]=useState(()=>`vax${Date.now()}${Math.floor(Math.random()*1e7)}`);
  const [firstDoseDate, setFirstDoseDate] = useState(today());
  const [statusFilter, setStatusFilter] = useState("");

  const [vaccines, setVaccines] = useState([]);
  const [batches, setBatches] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const selectedVaccine = useMemo(() => vaccines.find((v) => String(v.id) === String(vaccineId)), [vaccines, vaccineId]);
  const availableGroups=useMemo(()=>vaccineGroups.filter(g=>!g.archived && (g.vaccineIds||[]).length &&
    (g.vaccineIds||[]).every(id=>vaccines.some(v=>String(v.id)===String(id)))),[vaccineGroups,vaccines]);
  const selectedGroup=availableGroups.find(g=>String(g.id)===String(vaccineGroupId));
  const groupedIds=new Set(vaccineGroups.flatMap(g=>g.vaccineIds||[]).map(String));
  const legacyVaccines=vaccines.filter(v=>!groupedIds.has(String(v.id)));
  const plannedVaccines=selectedGroup?(selectedGroup.vaccineIds||[]).map(id=>vaccines.find(v=>String(v.id)===String(id))).filter(Boolean):
    (selectedVaccine?[selectedVaccine]:[]);


  async function loadDropdowns() {
    setError("");
    try {
      const [v, b, g, stock] = await Promise.all([
        listVaccines({ species, projectType, isActive: true }),
        listBatchesIdName(),
        vaccineGroupService.list(),
        inventoryService.getAllInventory(),
      ]);
      const vaccineStockItems=(stock||[]).filter(i=>(i.itemCategory||i.type)==="VACCINE"&&i.active!==false);
      setVaccines((v||[]).map(normalizeVaccine).filter(entry=>vaccineStockItems.some(i=>String(i.id)===String(entry.inventoryId))));
      setBatches(b||[]);
      setVaccineGroups(g||[]);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load vaccine data");
    }
  }

  async function loadTasks(showMessage = false) {
    setLoading(true);
    setError("");
    try {
      const rows = await listVaccineTasks(batchId || undefined, statusFilter || undefined);
      const filtered = (rows || []).filter((r) => {
        // Some task DTOs identify the animal/batch but omit species/project type.
        // Missing metadata must not hide a task that the API already returned.
        const sp = !species || !r.species || String(r.species).toUpperCase() === String(species).toUpperCase();
        const pt = sameProjectType(r.projectType, projectType);
        return sp && pt;
      });
      const combined = combineVaccineTasks(filtered);
      setTasks(combined);
      if (showMessage) setMessage(`Loaded ${combined.length} combined vaccine task(s).`);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load vaccine tasks");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDropdowns();
    setVaccineId("");setVaccineGroupId("");setRuleSelection("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [species, projectType]);

  useEffect(() => {
    loadTasks(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  async function schedule(e) {
    e.preventDefault();
    setError("");
    setMessage("");
    if(!ruleSelection)return setError("Select a saved vaccination rule.");
    if(selectionType==="SINGLE"&&!vaccineId)return setError("Select a legacy vaccine protocol.");
    if(selectionType==="GROUP"&&!selectedGroup)return setError("Select an active vaccination rule matching species and project.");
    if (!firstDoseDate) return setError("Select first dose date.");
    if (applyTo === "SPECIFIC" && !batchId) return setError("Select a specific batch or choose all ongoing batches.");

    setLoading(true);
    try {
      const request={
        vaccineId:selectionType==="SINGLE"?vaccineId:undefined,
        species,projectType,applyTo,
        batchId:applyTo==="SPECIFIC"?Number(batchId):undefined,
        firstDoseDate,
      };
      const created=selectionType==="GROUP"?
        await vaccineGroupService.schedule(Number(vaccineGroupId),{...request,requestKey:scheduleRequestKey}):
        await createVaccineSchedule(request);
      setScheduleRequestKey(`vax${Date.now()}${Math.floor(Math.random()*1e7)}`);
      const componentCount = Math.max(Number(created?.vaccinesScheduled)||1, 1);
      const combinedCount = Math.ceil((Number(created?.tasksCreated)||0) / componentCount);
      setMessage(selectionType==="GROUP"?
        `${created?.vaccinesScheduled||0} vaccines combined; ${combinedCount} animal task(s) created.`:
        `Vaccine scheduled successfully. ${created?.tasksCreated ?? 0} task(s) created.`);
      await loadTasks(false);
    } catch (e2) {
      setError(e2?.response?.data?.message || e2.message || "Failed to schedule vaccine");
    } finally {
      setLoading(false);
    }
  }

  async function taskAction(task, action) {
    setLoading(true);
    setError("");
    try {
      const taskIds = task.taskIds || [task.id];
      if (action === "given") {
        if (taskIds.length > 1) await markVaccineTasksGiven(taskIds);
        else await markVaccineTaskGiven(taskIds[0]);
      }
      if (action === "skip") {
        if (taskIds.length > 1) await skipVaccineTasks(taskIds);
        else await skipVaccineTask(taskIds[0]);
      }
      await loadTasks(false);
      setMessage(`${task.combinationName || task.vaccineName} combination updated together.`);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to update task");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sched-page">
      <style>{styles}</style>

      <div className="sched-tabs">
        <a className="sched-tab" href="/admin/vaccines">Vaccines</a>
        <a className="sched-tab active" href="/admin/vaccine-schedule">Schedule</a>
      </div>

      {error && <div className="sched-alert error">{error}</div>}
      {message && <div className="sched-alert success">{message}</div>}

      <div className="sched-info">
        <b>How scheduling works:</b> Select the saved vaccination rule created in Vaccine Setup, choose which animals to cover, and select the first dose date. All vaccines in the rule appear as one combined task per animal. Inventory is deducted for every component when the combination is marked Given.
      </div>

      <form className="sched-card" onSubmit={schedule}>
        <h3>Allocate Vaccination Rule</h3>
        <p>Assign a saved single- or multi-vaccine rule to your farm batches.</p>

        <section>
          <h4>Which animals?</h4>
          <div className="sched-grid two">
            <label>
              <span>Species</span>
              <select value={species} onChange={(e) => setSpecies(e.target.value)}>
                {SPECIES.map((s) => <option key={s} value={s}>{nice(s)}</option>)}
              </select>
            </label>
            <label>
              <span>Project type</span>
              <select value={projectType} onChange={(e) => setProjectType(e.target.value)}>
                {PROJECT_TYPES.map((p) => <option key={p} value={p}>{nice(p)}</option>)}
              </select>
            </label>
          </div>

          <label className="sched-field-block">
            <span>Apply to</span>
            <div className="sched-segments">
              <button type="button" className={applyTo === "ALL" ? "selected" : ""} onClick={() => setApplyTo("ALL")}>All ongoing batches</button>
              <button type="button" className={applyTo === "SPECIFIC" ? "selected" : ""} onClick={() => setApplyTo("SPECIFIC")}>A specific batch</button>
            </div>
            <small>{applyTo === "ALL" ? "All active batches matching the species and project type will be included." : "Only the selected batch will receive vaccine tasks."}</small>
          </label>

          {applyTo === "SPECIFIC" && (
            <label className="sched-wide-input">
              <span>Batch</span>
              <select value={batchId} onChange={(e) => setBatchId(e.target.value)}>
                <option value="">Select batch</option>
                {batches.map((b) => <option key={b.id} value={b.id}>#{b.id} — {b.batchName || b.name}</option>)}
              </select>
            </label>
          )}
        </section>

        <section>
          <h4>Saved vaccination rule &amp; date</h4>
          <div className="sched-grid two">
            <label>
              <span>Select saved vaccination rule *</span>
              <select value={ruleSelection} onChange={e=>{
                const value=e.target.value;setRuleSelection(value);
                if(value.startsWith("rule:")){setSelectionType("GROUP");setVaccineGroupId(value.slice(5));setVaccineId("");}
                else if(value.startsWith("legacy:")){setSelectionType("SINGLE");setVaccineId(value.slice(7));setVaccineGroupId("");}
                else {setVaccineId("");setVaccineGroupId("");}
                setScheduleRequestKey(`vax${Date.now()}${Math.floor(Math.random()*1e7)}`);
              }}>
                <option value="">Select saved rule</option>
                {availableGroups.map(g=><option key={g.id} value={`rule:${g.id}`}>{g.name} ({g.vaccineIds?.length||0} vaccine{g.vaccineIds?.length===1?"":"s"})</option>)}
                {legacyVaccines.length>0&&<optgroup label="Legacy individual protocols">
                  {legacyVaccines.map(v=><option key={v.id} value={`legacy:${v.id}`}>{v.vaccineName} (legacy)</option>)}
                </optgroup>}
              </select>
            </label>
            <label>
              <span>First vaccination date *</span>
              <input type="date" required value={firstDoseDate} onChange={e=>setFirstDoseDate(e.target.value)} />
            </label>
          </div>
          <small style={{display:"block",marginTop:10}}>First create vaccine stock in <a href="/admin/inventory">Inventory → Vaccine</a>, then save one vaccination rule in <a href="/admin/vaccines">Vaccine Setup</a>. Single and multi-vaccine rules use the same selection.</small>
          {plannedVaccines.length > 0 && <div className="sched-preview">
            <h4>Review vaccine administrations</h4>
            {plannedVaccines.map(v=><div className="sched-preview-row" key={v.id}>
              <span className="dot initial">✓</span><div><b>{v.vaccineName}</b>
                <small>{v.doseAmount} {v.doseUnit} {v.doseBasis==="PER_KG"?"per kg":"per animal"} · {nice(v.doseRoute)}</small>
                <small>{v.hasBooster?`Booster after ${v.boosterAfterDays} days`:"No booster"} · {v.hasRepeat?`Repeats every ${v.repeatEveryDays} days`:"One-time"}</small>
              </div>
            </div>)}
            <small>These vaccines are completed together as one animal task. Each component keeps its own dosage and inventory deduction.</small>
          </div>}
        </section>

        <div className="sched-actions">
          <button type="button" className="sched-btn ghost" onClick={() => { setVaccineId("");setVaccineGroupId("");setRuleSelection("");setScheduleRequestKey(`vax${Date.now()}${Math.floor(Math.random()*1e7)}`);setFirstDoseDate(today()); }}>Clear</button>
          <button type="submit" className="sched-btn success" disabled={loading}>{loading ? "Scheduling..." : "✓ Allocate Rule"}</button>
        </div>
      </form>

      <div className="sched-card tasks">
        <div className="sched-list-head">
          <div>
            <h3>Vaccine tasks</h3>
            <p>Mark each task as Given or Skip in this list. Repeat reminders appear here when due.</p>
          </div>
          <div className="task-filters">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUSES.map((s) => <option key={s || "all"} value={s}>{s ? nice(s) : "All statuses"}</option>)}
            </select>
            <button onClick={() => loadTasks(true)} disabled={loading}>Refresh</button>
          </div>
        </div>

        <div className="sched-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Batch</th>
                <th>Animal</th>
                <th>Vaccine</th>
                <th>Task type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 ? (
                <tr><td colSpan="7" className="sched-empty">{loading ? "Loading vaccine tasks..." : "No vaccine tasks found."}</td></tr>
              ) : tasks.map((t) => {
                const pending = ["PENDING", "TODAY", "MISSED"].includes(t.status);
                const upcoming = t.status === "UPCOMING";
                const done = ["GIVEN", "COMPLETED"].includes(t.status);
                return (
                  <tr key={t.id}>
                    <td>{formatDate(t.dueDate || t.taskDate)}</td>
                    <td><b>{t.batchName || "-"}</b></td>
                    <td>{t.animalName || t.animalTag || (t.animalId ? `Animal ${t.animalId}` : "All")}</td>
                    <td><b>{t.combinationName || t.vaccineName || "-"}</b></td>
                    <td><Badge kind={t.taskType === "BOOSTER" ? "booster" : t.taskType === "REPEAT_REMINDER" ? "repeat" : "initial"}>{typeLabel(t.taskType)}</Badge></td>
                    <td><Badge kind={done ? "given" : upcoming ? "upcoming" : t.status === "SKIPPED" ? "skip" : "pending"}>{statusLabel(t.status)}</Badge></td>
                    <td className="sched-row-actions">
                      {pending ? (
                        <>
                          <button className="ok" onClick={() => taskAction(t, "given")} disabled={loading}>Given</button>
                          <button onClick={() => taskAction(t, "skip")} disabled={loading}>Skip</button>
                        </>
                      ) : upcoming ? (
                        <span className="muted">Wait until due</span>
                      ) : (
                        <span className="muted">Done</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const styles = `
.sched-page{color:#22372c;max-width:1100px}.sched-tabs{display:flex;gap:9px;margin-bottom:22px}.sched-tab{color:#35654f;text-decoration:none;font-weight:700;padding:11px 18px;border:1px solid #d2e1d6;border-radius:9px}.sched-tab.active{background:#0a825b;color:white;border-color:#0a825b}.sched-alert{padding:14px;border-radius:10px;margin-bottom:16px}.sched-alert.error{background:#fff0ee;border:1px solid #ffc9c2;color:#ae2824}.sched-alert.success{background:#e9f7ee;border:1px solid #b2dfc3;color:#146842}.sched-info{background:#e9f6ef;border:1px solid #cbe8d6;color:#386c52;border-radius:10px;padding:16px;margin-bottom:24px}.sched-card{background:white;border:1px solid #dfebe2;border-radius:16px;padding:26px;margin-bottom:22px;box-shadow:0 6px 22px rgba(18,60,37,.05)}.sched-card h3{margin:0 0 8px;font-size:22px}.sched-card p{color:#798b7f}.sched-card section{border-top:1px solid #e1eee6;margin-top:22px;padding-top:20px}.sched-card h4{text-transform:uppercase;font-size:12px;letter-spacing:.08em;color:#65796c;margin:0 0 16px}.sched-grid{display:grid;gap:15px}.sched-grid.two{grid-template-columns:repeat(2,minmax(0,1fr))}.sched-card label{display:flex;flex-direction:column;gap:8px}.sched-card label span,.sched-field-block>span,.sched-wide-input>span{font-weight:750;font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:#5e7566}.sched-card input,.sched-card select,.sched-list-head select{background:white;color:#223b2e;border:1px solid #cbdfd0;border-radius:9px;padding:12px;font-size:14px;width:100%;min-width:0;box-sizing:border-box}.sched-card small,.muted{color:#7c9184}.sched-field-block,.sched-wide-input{margin-top:15px}.sched-segments{display:flex;gap:0;border:1px solid #d5e6d8;border-radius:9px;overflow:hidden;width:max-content}.sched-segments button{background:white;color:#526e5c;border:0;padding:12px 19px;cursor:pointer;font-weight:700}.sched-segments button.selected{background:#e6f4ed;color:#0d7850}.sched-preview{background:#f5faf6;border:1px solid #d8e8dc;border-radius:12px;padding:16px;margin-top:18px}.sched-preview-row{display:flex;align-items:center;gap:11px;padding:10px 0;border-bottom:1px solid #e1ebe4}.sched-preview-row .dot{width:25px;height:25px;border-radius:50%;background:#c2e8d1;color:#17693e;display:flex;align-items:center;justify-content:center}.sched-preview-row b{display:block}.sched-preview-row small{display:block;margin-top:3px}.sched-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:20px}.sched-btn{border-radius:8px;border:1px solid #ccded0;padding:11px 20px;font-weight:800;cursor:pointer}.sched-btn.ghost{background:white;color:#3e5e4b}.sched-btn.success{background:#0c8059;color:white;border-color:#0c8059}.sched-btn:disabled{opacity:.6}.sched-list-head{display:flex;justify-content:space-between;align-items:center;gap:16px;margin-bottom:18px}.task-filters{display:flex;gap:9px}.task-filters button{background:white;border:1px solid #c9d9ce;border-radius:8px;padding:10px;color:#365846}.sched-table-wrap{overflow:auto}.sched-table-wrap table{border-collapse:collapse;width:100%}.sched-table-wrap th{background:#f2f7f3;color:#6b8271;text-align:left;text-transform:uppercase;font-size:11px;padding:13px}.sched-table-wrap td{border-top:1px solid #e6eee8;padding:14px}.sched-badge{padding:6px 11px;border-radius:999px;background:#e8f1ed;color:#426757;display:inline-block;font-size:12px;font-weight:700}.sched-badge.pending{background:#fff5e1;color:#a16b0e}.sched-badge.given{background:#e4f7ec;color:#0d8055}.sched-badge.skip{background:#feeeea;color:#b43f32}.sched-row-actions button{border:1px solid #c8ddce;background:white;border-radius:7px;padding:8px 12px;color:#2a6950;cursor:pointer;margin-right:5px}.sched-empty{text-align:center;color:#7e9284}@media(max-width:750px){.sched-grid.two{grid-template-columns:1fr}.sched-card{padding:16px}.sched-list-head{flex-direction:column;align-items:stretch}.sched-tabs{flex-wrap:wrap}.task-filters{flex-wrap:wrap}}
`;
